import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup, signOut, User, createUserWithEmailAndPassword, signInWithEmailAndPassword, UserCredential, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../config/firebase';
import axios from 'axios';
import { defaultRetryPolicy } from '../utils/retryPolicy';
import { captureException } from '../utils/captureException';

interface UserData {
  access: boolean;
  username: string;
  role: string;
  email: string;
  language: string;
}

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<UserCredential>;
  signInWithMicrosoft: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<UserCredential>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  setDoFetchUserData: (value: boolean) => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [doFetchUserData, setDoFetchUserData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setLoading(true);
      setError(null);
      
      if (user) {
        setUser(user);
        try {
          const idToken = await user.getIdToken();
          const apiUrl = process.env.REACT_APP_API_URL || '';
          // Add retry policy:
          const response = await defaultRetryPolicy.executeWithRetry(async () => {
            return await axios.get(`${apiUrl}/api/user/me`, {
              headers: { Authorization: `Bearer ${idToken}` },
              timeout: 5000 // 5 second timeout
            });
          }, 'Fetch user data API call', {
            apiUrl,
            userId: user.uid,
          });
          
          if (response.data.access) {
            setUserData(response.data);
          } else {
            console.log("Response data:", response.data);
            setError('User access denied');
            await signOut(auth);
            navigate('/login');
          }
        } catch (error: any) {
          captureException(error, { tags: { feature: 'auth' }, extra: { action: 'onAuthStateChanged' } });
          console.error('Auth error:', error);
          setError(error.message || 'Failed to authenticate user');
          
          // User is logged out
          setUser(null);
          setUserData(null);
        }
      } else {
        // User is logged out
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const idToken = await user?.getIdToken();
        const apiUrl = process.env.REACT_APP_API_URL || '';
        
        const response = await defaultRetryPolicy.executeWithRetry(async () => {
          return await axios.get(`${apiUrl}/api/user/me`, {
            headers: { Authorization: `Bearer ${idToken}` },
            timeout: 5000
          });
        }, 'Fetch user data API call', {
          apiUrl,
          userId: user?.uid,
        });
        setUserData(response.data);
      } catch (error: any) {
        captureException(error, { tags: { feature: 'auth' }, extra: { action: 'fetchUserData' } });
        console.error('Error fetching user data:', error);
        setError(error.message || 'Failed to fetch user data');
      }
    };
    
    if (doFetchUserData) {
      fetchUserData();
      setDoFetchUserData(false);
    }
  }, [doFetchUserData, user]);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const user = await signInWithPopup(auth, provider);
      return user;
      // The onAuthStateChanged listener will handle the post-login logic
    } catch (error) {
      captureException(error, { tags: { feature: 'auth' }, extra: { action: 'signInWithGoogle' } });
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signInWithMicrosoft = async () => {
    const provider = new OAuthProvider('microsoft.com');
    try {
      await signInWithPopup(auth, provider);
      // The onAuthStateChanged listener will handle the post-login logic
    } catch (error) {
      captureException(error, { tags: { feature: 'auth' }, extra: { action: 'signInWithMicrosoft' } });
      console.error('Error signing in with Microsoft:', error);
      throw error;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // The onAuthStateChanged listener will handle the post-login logic
    } catch (error) {
      captureException(error, { tags: { feature: 'auth' }, extra: { action: 'signInWithEmail' } });
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      const user = await createUserWithEmailAndPassword(auth, email, password);
      return user;
      // The onAuthStateChanged listener will handle the post-login logic
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      captureException(error, { tags: { feature: 'auth' }, extra: { action: 'resetPassword' } });
      console.error('Error sending password reset email:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      navigate('/login'); // Redirect to login on logout
    } catch (error) {
      captureException(error, { tags: { feature: 'auth' }, extra: { action: 'logout' } });
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    userData,
    loading,
    error,
    signInWithGoogle,
    signInWithMicrosoft,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    logout,
    setDoFetchUserData,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 